import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Paperclip,
  Upload,
  Download,
  Trash2,
  File,
  FileText,
  FileImage,
  FileArchive,
  FileSpreadsheet,
  Loader2,
  X,
  Plus,
} from 'lucide-react';
import { documentationAPI } from '../../services/api';
import { useAuthStore } from '../../context/authStore';
import { isInternalRole } from '../../utils/roles.js';
import { notifyError } from '../../context/toastStore';

// Get icon for file type
function getFileIcon(mimeType) {
  if (mimeType?.startsWith('image/')) return FileImage;
  if (mimeType?.includes('pdf')) return FileText;
  if (mimeType?.includes('spreadsheet') || mimeType?.includes('excel') || mimeType?.includes('csv')) return FileSpreadsheet;
  if (mimeType?.includes('zip') || mimeType?.includes('rar') || mimeType?.includes('7z')) return FileArchive;
  return File;
}

// Format file size
function formatFileSize(bytes) {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

/**
 * Attachments Component
 * Displays and manages file attachments for assets
 */
export default function Attachments({ assetType, assetId, companyId }) {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const canEdit = isInternalRole(user?.role);
  const fileInputRef = useRef(null);

  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  // Fetch attachments
  const { data, isLoading, error } = useQuery({
    queryKey: ['attachments', assetType, assetId],
    queryFn: async () => {
      const response = await documentationAPI.listAttachments(assetType, assetId, { companyId });
      return response.data.attachments || [];
    },
    enabled: !!assetType && !!assetId,
  });

  const attachments = data || [];

  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: async (file) => {
      const formData = new FormData();
      formData.append('file', file);
      return documentationAPI.uploadAttachment(assetType, assetId, formData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['attachments', assetType, assetId] });
      setUploadError(null);
    },
    onError: (err) => {
      setUploadError(err.response?.data?.error || 'Upload failed');
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id) => documentationAPI.deleteAttachment(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['attachments', assetType, assetId] });
    },
  });

  // Handle file upload
  const handleUpload = async (files) => {
    setUploading(true);
    setUploadError(null);

    for (const file of files) {
      await uploadMutation.mutateAsync(file);
    }

    setUploading(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle file input change
  const handleFileChange = (e) => {
    if (e.target.files?.length) {
      handleUpload(Array.from(e.target.files));
    }
  };

  // Handle drag and drop
  const handleDragOver = (e) => {
    e.preventDefault();
    if (canEdit) setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (canEdit && e.dataTransfer.files?.length) {
      handleUpload(Array.from(e.dataTransfer.files));
    }
  };

  // Handle download
  const handleDownload = async (attachment) => {
    try {
      const response = await documentationAPI.downloadAttachment(attachment.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', attachment.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      notifyError('The file could not be downloaded.', err);
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6">
        <div className="flex items-center gap-2 text-neutral-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading attachments...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6">
        <p className="text-red-600 dark:text-red-400">Failed to load attachments</p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200 dark:border-neutral-700">
        <div className="flex items-center gap-2">
          <Paperclip className="w-5 h-5 text-neutral-500 dark:text-neutral-400" />
          <h3 className="text-lg font-medium text-neutral-900 dark:text-white">
            Attachments
          </h3>
          {attachments.length > 0 && (
            <span className="px-2 py-0.5 text-xs font-medium bg-neutral-100 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-300 rounded-full">
              {attachments.length}
            </span>
          )}
        </div>
        {canEdit && (
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors disabled:opacity-50"
          >
            {uploading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Plus className="w-4 h-4" />
            )}
            Upload
          </button>
        )}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileChange}
          className="hidden"
        />
      </div>

      {/* Content */}
      <div
        className={`p-6 ${dragOver ? 'bg-primary-50 dark:bg-primary-900/10' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {uploadError && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm flex items-center justify-between">
            <span>{uploadError}</span>
            <button onClick={() => setUploadError(null)}>
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {attachments.length === 0 ? (
          <div
            className={`text-center py-8 border-2 border-dashed rounded-lg transition-colors ${
              dragOver
                ? 'border-primary-400 dark:border-primary-500'
                : 'border-neutral-200 dark:border-neutral-700'
            }`}
          >
            <Paperclip className="w-12 h-12 mx-auto text-neutral-300 dark:text-neutral-600 mb-3" />
            <p className="text-neutral-500 dark:text-neutral-400">No attachments</p>
            {canEdit && (
              <p className="text-sm text-neutral-400 dark:text-neutral-500 mt-1">
                Drag and drop files here or click Upload
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            {attachments.map((attachment) => {
              const FileIcon = getFileIcon(attachment.mimeType);

              return (
                <div
                  key={attachment.id}
                  className="flex items-center justify-between p-3 bg-neutral-50 dark:bg-neutral-700/50 rounded-lg group"
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-10 h-10 rounded-lg bg-white dark:bg-neutral-600 border border-neutral-200 dark:border-neutral-500 flex items-center justify-center flex-shrink-0">
                      <FileIcon className="w-5 h-5 text-neutral-500 dark:text-neutral-300" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-neutral-900 dark:text-white truncate">
                        {attachment.filename}
                      </p>
                      <p className="text-xs text-neutral-500 dark:text-neutral-400">
                        {formatFileSize(attachment.fileSize)}
                        {attachment.uploadedBy && (
                          <> • Uploaded by {attachment.uploadedBy.firstName} {attachment.uploadedBy.lastName}</>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleDownload(attachment)}
                      className="p-2 text-neutral-400 hover:text-primary-600 dark:hover:text-primary-400 rounded transition-colors"
                      title="Download"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                    {canEdit && (
                      <button
                        onClick={() => deleteMutation.mutate(attachment.id)}
                        disabled={deleteMutation.isPending}
                        className="p-2 text-neutral-400 hover:text-red-600 dark:hover:text-red-400 rounded transition-colors opacity-0 group-hover:opacity-100"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Drop zone overlay when dragging */}
        {dragOver && canEdit && (
          <div className="absolute inset-0 flex items-center justify-center bg-primary-500/10 border-2 border-dashed border-primary-500 rounded-lg">
            <div className="text-center">
              <Upload className="w-12 h-12 mx-auto text-primary-500 mb-2" />
              <p className="text-primary-600 dark:text-primary-400 font-medium">Drop files to upload</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
